import { Container } from '../components/Layout.tsx';
import { Card, PageTitle } from '../components/ui.tsx';
import { useApi, useTitle } from '../hooks.ts';

function useSite() {
  return useApi<{
    name: string;
    organisation: string;
    contactEmail: string;
    inactiveAccountMonths: number;
    privacy?: {
      aiFeedback: boolean;
      pwnedPasswords: boolean;
      virusScanning: boolean;
      outboxDays: number;
      auditMonths: number;
    };
  }>('/api/site').data;
}

export function PrivacyPage() {
  useTitle('Personuppgifter och kakor');
  const site = useSite();
  const p = site?.privacy;
  return (
    <Container>
      <PageTitle>Personuppgifter och kakor</PageTitle>
      <Card className="prose-aurora max-w-3xl">
        <p>
          Aurora drivs av {site?.organisation ?? 'Luleå tekniska universitet'} inom projektet AURORA –
          AI-litteracitet för lärare i Norrbotten. Här beskriver vi vilka uppgifter vi sparar och varför.
        </p>
        <h2>Vilka uppgifter sparas?</h2>
        <ul>
          <li>Namn, e-postadress, kommun, skolform och – om du anger det – skola och ämne.</li>
          <li>Ditt lösenord, krypterat (hashat) så att ingen kan läsa det.</li>
          <li>
            Vilka delar du öppnat och gjort, quizresultat, diskussionsinlägg och kamratåterkoppling,
            inlämningar och lärarens återkoppling.
          </li>
          <li>Utfärdade intyg, vilken grupp du hör till och när du senast var aktiv.</li>
          <li>
            När du loggat in och på vilket sätt (sparas i ett år), så att vi kan hjälpa dig om något krånglar.
          </li>
          <li>Mejl som Aurora skickat till dig (sparas i {p?.outboxDays ?? 90} dagar).</li>
          <li>Anteckningar i Min AI-dagbok. Dem ser bara du – inte kursens lärare eller andra deltagare.</li>
        </ul>
        <h2>Varför?</h2>
        <p>
          För att du ska kunna gå kursen, få återkoppling och intyg, och för att vi ska kunna följa upp hur
          kursen används i länets kommuner. Statistik till kommuner och finansiärer redovisas sammanställt,
          utan namn.
        </p>
        <h2>Vem ser vad?</h2>
        <ul>
          <li>Kursens lärare ser dina uppgifter och resultat.</li>
          <li>
            Rättare som kursen anlitar ser och rättar inlämningar från deltagarna i sina grupper, men inte din
            dagbok.
          </li>
          <li>
            Kontaktpersoner i kommunerna ser bara sammanställd statistik för sin kommun, aldrig namn. Grupper
            med färre än tre deltagare visas inte.
          </li>
          <li>
            Andra deltagare i samma kursomgång ser ditt förnamn, första bokstaven i efternamnet, din kommun
            och dina diskussionsinlägg.
          </li>
        </ul>
        <h2>Tjänster utanför Aurora</h2>
        <ul>
          {p?.pwnedPasswords !== false && (
            <li>
              När du väljer lösenord kontrolleras det mot kända dataläckor (Have I Been Pwned). Bara fem
              tecken av en krypterad sammanfattning av lösenordet skickas – aldrig lösenordet eller din
              e-postadress.
            </li>
          )}
          {p?.aiFeedback && (
            <li>
              Lärare kan be AI-tjänsten Claude (Anthropic) om ett förslag på återkoppling. Då skickas
              uppgiftens text och din inlämnings text, men inte ditt namn eller din e-postadress. Läraren
              läser och godkänner alltid återkopplingen. Enligt Anthropics villkor för tjänsten används texten
              inte för att träna AI-modeller.
            </li>
          )}
          {p?.virusScanning && (
            <li>Uppladdade filer kontrolleras mot virus på Auroras egen server. Inget skickas vidare.</li>
          )}
          <li>Filmerna visas från YouTube (se nedan).</li>
        </ul>
        <h2>Hur länge sparas uppgifterna?</h2>
        <p>
          Så länge du har ett konto. Tar du bort kontot försvinner dina uppgifter, inlägg och inlämningar
          direkt, liksom mejl till dig. Säkerhetskopior gallras efter två veckor. Händelseloggen (t.ex. att
          ett konto tagits bort) sparas i {p?.auditMonths ?? 24} månader, utan namn och e-postadresser.
        </p>
        <h2>Kontaktformuläret</h2>
        <p>
          Skriver du till oss via kontaktformuläret skickas ditt namn, din e-postadress och ditt meddelande
          som ett mejl till kursens kontaktadress. Vi använder det bara för att svara dig.
        </p>
        <h2>Kakor och filmer</h2>
        <p>
          Aurora använder en enda kaka, som håller dig inloggad. Filmerna ligger på YouTube och visas i
          YouTubes integritetsläge (youtube-nocookie.com). Spelaren laddas när du öppnar en del med film, även
          om du inte spelar den. Din webbläsare kontaktar då YouTube, som kan spara uppgifter i din webbläsare
          enligt sina villkor.
        </p>
        <h2>Dina rättigheter</h2>
        <p>
          Under Mitt konto kan du ladda ned alla uppgifter vi har om dig (registerutdrag), rätta dina
          uppgifter och ta bort ditt konto.
          {site?.inactiveAccountMonths
            ? ` Konton som inte använts på ${site.inactiveAccountMonths} månader tas bort automatiskt; du får ett mejl 30 dagar innan. Intyg går fortfarande att verifiera efteråt (namn, kurs, datum och kod).`
            : ''}{' '}
          Frågor besvaras av <a href={`mailto:${site?.contactEmail ?? ''}`}>{site?.contactEmail}</a>.
        </p>
      </Card>
    </Container>
  );
}

export function AccessibilityPage() {
  useTitle('Tillgänglighet');
  const site = useSite();
  return (
    <Container>
      <PageTitle>Tillgänglighet</PageTitle>
      <Card className="prose-aurora max-w-3xl">
        <p>
          Aurora ska gå att använda för alla, med tangentbord, skärmläsare och på mobilen. Vi strävar efter
          att uppfylla WCAG 2.1 nivå AA.
        </p>
        <p>
          Hittar du något som inte fungerar? Skriv till{' '}
          <a href={`mailto:${site?.contactEmail ?? ''}`}>{site?.contactEmail}</a> så rättar vi det.
        </p>
      </Card>
    </Container>
  );
}
